import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, ShieldX, Search } from "lucide-react";

export default function MasterExclusion() {
  const [type, setType] = useState("email");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runCheck = async (overrideType) => {
    setError(""); setResult(null);
    const checkType = overrideType || type;
    if (!value.trim()) { setError("Enter a value to check."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/exclusion/master", { type: checkType, value });
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Tool · Database-wide"
        title="Master Exclusion Check"
        description="Search every campaign in the database. Returns the matching leads if the value is excluded."
        testid="master-exclusion-header"
      />
      <div className="p-8 space-y-6 max-w-5xl">
        <Tabs value={type} onValueChange={(v) => { setType(v); setResult(null); setError(""); }}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="email" data-testid="tab-search-email" className="rounded-sm">Search by Email</TabsTrigger>
            <TabsTrigger value="domain" data-testid="tab-search-domain" className="rounded-sm">Search by Domain</TabsTrigger>
          </TabsList>
          <TabsContent value="email" className="mt-6">
            <SearchBox
              placeholder="e.g. john.doe@acme.com"
              value={value} setValue={setValue} loading={loading}
              onRun={() => runCheck("email")} testidInput="master-email-input" testidBtn="master-email-run"
            />
          </TabsContent>
          <TabsContent value="domain" className="mt-6">
            <SearchBox
              placeholder="e.g. acme.com"
              value={value} setValue={setValue} loading={loading}
              onRun={() => runCheck("domain")} testidInput="master-domain-input" testidBtn="master-domain-run"
            />
          </TabsContent>
        </Tabs>

        {error && <div data-testid="master-error" className="badge-error px-4 py-3 rounded-sm text-sm">{error}</div>}

        {result && <ResultPanel result={result} testid="master-result" />}
      </div>
    </div>
  );
}

function SearchBox({ placeholder, value, setValue, loading, onRun, testidInput, testidBtn }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input
          data-testid={testidInput}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="h-14 rounded-sm pl-10 font-mono text-base"
          onKeyDown={(e) => e.key === "Enter" && onRun()}
        />
      </div>
      <Button
        data-testid={testidBtn}
        onClick={onRun}
        disabled={loading}
        className="h-14 rounded-sm bg-primary hover:bg-[#00227A] px-8"
      >
        {loading ? "Checking…" : "Run Check"}
      </Button>
    </div>
  );
}

export function ResultPanel({ result, testid }) {
  const excluded = result.excluded;
  return (
    <div className="border border-border" data-testid={testid}>
      <div className={`px-6 py-4 flex items-center gap-3 border-b border-border ${excluded ? "bg-red-50" : "bg-emerald-50"}`}>
        {excluded ? <ShieldX className="text-red-600" /> : <ShieldCheck className="text-emerald-700" />}
        <div>
          <div className="meta-label">{excluded ? "Excluded" : "Clean"}</div>
          <div className="font-display font-bold text-lg">
            {excluded ? `Found in database · ${result.match_count} match${result.match_count > 1 ? "es" : ""}` : "No exclusion match"}
          </div>
        </div>
        <div className="ml-auto font-mono text-xs text-muted-foreground">{result.type}: {result.value}</div>
      </div>
      {excluded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border bg-secondary/40">
                <th className="px-4 py-2 meta-label">Lead ID</th>
                <th className="px-4 py-2 meta-label">Name</th>
                <th className="px-4 py-2 meta-label">Email</th>
                <th className="px-4 py-2 meta-label">Domain</th>
                <th className="px-4 py-2 meta-label">Campaign</th>
              </tr>
            </thead>
            <tbody>
              {result.matches.map((m) => (
                <tr key={m.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-mono text-xs">{m.lead_id || m.id.slice(0,8)}</td>
                  <td className="px-4 py-2">{m.first_name} {m.last_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{m.work_email}</td>
                  <td className="px-4 py-2 font-mono text-xs">{m.domain}</td>
                  <td className="px-4 py-2">{m.campaign_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
